
import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button'; // Fixed import
import { Check, Copy, RefreshCw } from 'lucide-react';

type ErrorLog = {
  id: string;
  message: string;
  stack: string | null;
  user_id: string | null;
  created_at: string;
  resolved: boolean;
  path: string | null;
};

const ErrorsTab = () => {
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  React.useEffect(() => {
    fetchErrorLogs();
    
    // Set up polling for error logs
    const intervalId = setInterval(fetchErrorLogs, 30000); // Poll every 30 seconds
    
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const fetchErrorLogs = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('error_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setErrorLogs(data || []);
    } catch (error) {
      console.error('Error fetching error logs:', error);
      // If we have a real error, show an error toast
      toast({
        title: 'Fehler',
        description: 'Fehler beim Laden der Fehlerprotokolle.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resolveError = async (errorId: string) => {
    try {
      const { error } = await supabase
        .from('error_logs')
        .update({ resolved: true })
        .eq('id', errorId);
      
      if (error) throw error;
      
      // Refresh the data
      await fetchErrorLogs();
      
      toast({
        title: 'Fehler behoben',
        description: 'Der Fehler wurde als behoben markiert.',
      });
    } catch (error) {
      console.error('Error resolving error log:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Markieren als behoben.',
        variant: 'destructive',
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Kopiert',
      description: 'In die Zwischenablage kopiert.',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fehlerprotokolle</CardTitle>
        <CardDescription>
          Verfolgen Sie Fehler, die im System aufgetreten sind.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-jade" />
            <p>Fehlerprotokolle werden geladen...</p>
          </div>
        ) : errorLogs.length === 0 ? (
          <Alert>
            <AlertDescription>
              Keine Fehlerprotokolle gefunden.
            </AlertDescription>
          </Alert>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fehler</TableHead>
                <TableHead>Pfad</TableHead>
                <TableHead>Benutzer</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {errorLogs.map((error) => (
                <TableRow key={error.id}>
                  <TableCell className="font-medium">{error.message}</TableCell>
                  <TableCell>{error.path || <em className="text-gray-500">Unbekannt</em>}</TableCell>
                  <TableCell>{error.user_id || <em className="text-gray-500">Anonym</em>}</TableCell>
                  <TableCell>{formatDate(error.created_at)}</TableCell>
                  <TableCell>
                    {error.resolved ? (
                      <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
                        Behoben
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
                        Offen
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(error.stack || error.message)}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Stack
                      </Button>
                      
                      {!error.resolved && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resolveError(error.id)}
                          className="bg-green-500/10 hover:bg-green-500/20 border-green-500/30"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Beheben
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default ErrorsTab;
